import React, { useMemo } from "react";
import { useGetTransportTypesList } from "../../../../../api/static-data";
import { FormInput } from "../../../../../components/ui/Input/FormInput/FormInput";
import { FormInputNumber } from "../../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../../components/ui/Input/FormSelector/FormSelect";
import { FormUnitInput } from "../../../../../components/ui/Input/FormUnitInput/FormUnitInput";
import { ExternalTransportRecord } from "../../../../../types/prodcut-data-review/product-data.types";
import { DEFAULT_FORM_VALUES } from "../constants/externalTransportTable.constants";
import { WEIGHT_UNIT_OPTIONS } from "../constants/transportTable.constants";
import { ExternalTransportFormValues } from "../types/externalTransportTable.types";
import { fromFormValues, toFormValues } from "../utils/externalTransportTable.utils";
import EditableTable, { ColumnDef } from "../../shared/components/EditableTable";

// ── Props ────────────────────────────────────────────────────────────────────
interface ExternalTransportTableProps {
  records: ExternalTransportRecord[];
  onSave: (id: string, patch: Partial<ExternalTransportRecord>) => void;
  onEdit?: (id: string) => void;
  onCancel?: (id: string) => void;
  disabled?: boolean;
}

// ── Component ────────────────────────────────────────────────────────────────
const ExternalTransportTable: React.FC<ExternalTransportTableProps> = ({
  records,
  onSave,
  onEdit,
  onCancel,
  disabled = false
}) => {
  const { data: transportTypes } = useGetTransportTypesList();

  const transportTypeOptions = useMemo(
    () => (transportTypes ?? []).map((t: any) => ({ value: t.text as string, label: t.text as string })),
    [transportTypes]
  );

  const columns = useMemo<ColumnDef<ExternalTransportRecord, ExternalTransportFormValues>[]>(
    () => [
      {
        id: "name",
        header: "Name",
        cell: r => <span className="tw-text-sm tw-font-medium">{r.name || "—"}</span>,
        editCell: ({ control }) => (
          <FormInput control={control} name="name" label="" className="tw-h-8 tw-text-xs" />
        )
      },
      {
        id: "transportType",
        header: "Type",
        cell: r => <span className="tw-text-sm">{r.transportType || "—"}</span>,
        editCell: ({ control }) => (
          <FormSelect
            control={control}
            name="transportType"
            label=""
            placeholder="Select"
            options={transportTypeOptions}
          />
        )
      },
      {
        id: "from",
        header: "From",
        cell: r => <span className="tw-text-sm">{r.from || "—"}</span>,
        editCell: ({ control }) => (
          <FormInput control={control} name="from" label="" className="tw-h-8 tw-text-xs" />
        )
      },
      {
        id: "to",
        header: "To",
        cell: r => <span className="tw-text-sm">{r.to || "—"}</span>,
        editCell: ({ control }) => (
          <FormInput control={control} name="to" label="" className="tw-h-8 tw-text-xs" />
        )
      },
      {
        id: "weight",
        header: "Weight",
        cell: r => (
          <span className="tw-text-sm tw-font-mono">
            {r.weight !== "" ? `${r.weight} ${r.weightUnit}` : "—"}
          </span>
        ),
        editCell: ({ control }) => (
          <FormUnitInput
            control={control}
            name="weight"
            label=""
            unitName="weightUnit"
            unitOptions={WEIGHT_UNIT_OPTIONS}
          />
        )
      },
      {
        id: "distance",
        header: "Distance (km)",
        headerClassName: "tw-text-right",
        cellClassName: "tw-text-right",
        cell: r => (
          <span className="tw-text-sm tw-font-mono">{r.distance !== "" ? String(r.distance) : "—"}</span>
        ),
        editCell: ({ control }) => (
          <FormInputNumber
            control={control}
            name="distance"
            label=""
            className="tw-h-8 tw-text-xs tw-font-mono tw-w-24 tw-ml-auto"
          />
        )
      }
    ],
    [transportTypeOptions]
  );

  return (
    <EditableTable
      records={records}
      columns={columns}
      defaultFormValues={DEFAULT_FORM_VALUES}
      toFormValues={toFormValues}
      fromFormValues={fromFormValues}
      onSave={onSave}
      onEdit={onEdit}
      onCancel={onCancel}
      emptyLabel="external transports"
      disabled={disabled}
    />
  );
};

export default ExternalTransportTable;
